import { useMemo } from 'react';

import { uniqBy } from 'lodash';

import { useBrandsQuery } from 'src/app/queries/brand/get-brands';
import { useCategoriesQuery } from 'src/app/queries/category/get-categories';
import { useTagsQuery } from 'src/app/queries/configuration/tags/get-tags';
import { useChargeCodesQuery } from 'src/app/queries/discounts/get-charge-codes';
import { useDiscountProductsQuery } from 'src/app/queries/discounts/get-discount-products';
import { useStrainsQuery } from 'src/app/queries/strain/get-strains';
import { useVendorsQuery } from 'src/app/queries/vendors/get-vendors';

import { FILTER_RESOURCES } from './constants';

import type { FilterResource, FilterResourceType } from './filter';
import type { DiscountProduct } from 'src/app/queries/discounts/get-discount-products';

type AvailableProductResource = Record<number, number>;
type AvailableProductsByResource = Record<string, AvailableProductResource>;
export type ProductActiveFilterResources = DiscountProduct & FilterResource;
type ActiveFilterResources = {
  activeFilterResources: FilterResource[] | ProductActiveFilterResources[];
  loading: boolean;
};

export function useFilterResourceData({
  filterResourceType,
}: {
  filterResourceType: FilterResourceType;
}): ActiveFilterResources {
  const { data: strains, isLoading: isStrainsLoading } = useStrainsQuery();
  const { data: products, isLoading: isProductsLoading } = useDiscountProductsQuery();
  const { data: categories, isLoading: isCategoriesLoading } = useCategoriesQuery();
  const { data: tags, isLoading: isTagsLoading } = useTagsQuery();
  const { data: chargeCodes, isLoading: isChargeCodesLoading } = useChargeCodesQuery();
  const { data: brands, isLoading: isBrandsLoading } = useBrandsQuery();
  const { data: vendors, isLoading: isVendorsLoading } = useVendorsQuery();
  const weights = uniqBy(products ?? [], 'Weight').filter(
    (product) => product.Weight !== undefined && product.Weight !== null
  );

  const availableProductsByResource: AvailableProductsByResource = useMemo(
    () =>
      (products ?? []).reduce(
        (accumulator, currentValue) => {
          // STRAINS
          const strainId = currentValue.StrainId;
          if (strainId) {
            const currentStrainCount: number = accumulator.strains[strainId] ?? 0;
            accumulator.strains[strainId] = currentStrainCount + 1;
          }

          // CATEGORIES
          const categoryId = currentValue.CategoryId;
          if (categoryId) {
            const currentCategoryCount: number = accumulator.categories[categoryId] ?? 0;
            accumulator.categories[categoryId] = currentCategoryCount + 1;
          }

          // TAGS
          const tagIds = currentValue.TagIds ?? [];
          tagIds.forEach((tagId) => {
            if (tagId) {
              const currentTagCount: number = accumulator.tags[tagId] ?? 0;
              accumulator.tags[tagId] = currentTagCount + 1;
            }
          });

          // INVENTORY TAGS
          const inventoryTagIds = currentValue.InventoryTagIds ?? [];
          inventoryTagIds.forEach((inventoryTagId) => {
            if (inventoryTagId) {
              const currentInventoryTagCount: number = accumulator.inventoryTags[inventoryTagId] ?? 0;
              accumulator.inventoryTags[inventoryTagId] = currentInventoryTagCount + 1;
            }
          });

          // CHARGE CODES
          const chargeCodeId = currentValue.ChargeCodeId;
          if (chargeCodeId) {
            const currentChargeCodeCount: number = accumulator.chargeCodes[chargeCodeId] ?? 0;
            accumulator.chargeCodes[chargeCodeId] = currentChargeCodeCount + 1;
          }

          // BRANDS
          const brandId = currentValue.BrandId;
          if (brandId) {
            const currentBrandCount: number = accumulator.brands[brandId] ?? 0;
            accumulator.brands[brandId] = currentBrandCount + 1;
          }

          // VENDORS
          const vendorId = currentValue.VendorId;
          if (vendorId) {
            const currentVendorCount: number = accumulator.vendors[vendorId] ?? 0;
            accumulator.vendors[vendorId] = currentVendorCount + 1;
          }

          // WEIGHTS
          const weightValue = currentValue.Weight;
          if (weightValue) {
            const currentWeightCount: number = accumulator.weights[weightValue] ?? 0;
            accumulator.weights[weightValue] = currentWeightCount + 1;
          }

          return accumulator;
        },
        {
          strains: {},
          categories: {},
          tags: {},
          chargeCodes: {},
          brands: {},
          vendors: {},
          weights: {},
          inventoryTags: {},
        }
      ),
    [products]
  );

  const strainsAsFilterResources = useMemo(
    () =>
      (strains ?? []).map((strain) => ({
        resourceName: strain.StrainName || '',
        resourceId: strain.StrainId,
        resourceNumberOfProducts: availableProductsByResource.strains[strain.StrainId] ?? 0,
      })),
    [strains]
  );

  const categoriesAsFilterResources = useMemo(
    () =>
      (categories ?? []).map((category) => ({
        resourceName: category.ProductCategory,
        resourceId: category.ProductCategoryId,
        masterCategory: category.MasterCategory,
        resourceNumberOfProducts: availableProductsByResource.categories[category.ProductCategoryId] ?? 0,
      })),
    [categories]
  );

  const weightsAsFilterResources = useMemo(
    () =>
      weights.map((product) => ({
        resourceName: String(product.Weight),
        resourceId: product.Weight,
        resourceNumberOfProducts: availableProductsByResource.weights[product.Weight] ?? 0,
      })),
    [products]
  );

  const tagsAsFilterResources = useMemo(
    () =>
      (tags ?? []).map((tag) => ({
        resourceName: tag.TagName,
        resourceId: tag.TagId,
        resourceNumberOfProducts: availableProductsByResource.tags[tag.TagId] ?? 0,
      })),
    [tags]
  );

  const inventoryTagsAsFilterResources = useMemo(
    () =>
      (tags ?? []).map((tag) => ({
        resourceName: tag.TagName,
        resourceId: tag.TagId,
        resourceNumberOfProducts: availableProductsByResource.inventoryTags[tag.TagId] ?? 0,
      })),
    [tags]
  );

  const chargeCodesAsFilterResources = useMemo(
    () =>
      (chargeCodes ?? []).map((chargeCode) => ({
        resourceName: chargeCode.ChargeCodeName,
        resourceId: chargeCode.ChargeCodeId,
        resourceNumberOfProducts: availableProductsByResource.chargeCodes[chargeCode.ChargeCodeId] ?? 0,
      })),
    [chargeCodes]
  );

  const brandsAsFilterResources = useMemo(
    () =>
      (brands ?? []).map((brand) => ({
        resourceName: brand.BrandName,
        resourceId: brand.BrandId,
        resourceNumberOfProducts: availableProductsByResource.brands[brand.BrandId] ?? 0,
      })),
    [brands]
  );

  const vendorsAsFilterResources = useMemo(
    () =>
      (vendors ?? []).map((vendor) => ({
        resourceName: vendor.VendorName || '',
        resourceId: vendor.VendorId,
        resourceNumberOfProducts: availableProductsByResource.vendors[vendor.VendorId] ?? 0,
      })),
    [vendors]
  );

  const productsAsFilterResources = useMemo(
    () =>
      (products ?? [])
        .filter((product) => !product.IsRetired || product.Available > 0)
        .map((product) => ({
          resourceName: product.ProductDesc,
          resourceId: product.ProductId,
          resourceNumberOfProducts: 0,
          ...product,
        })),
    [products]
  );

  switch (filterResourceType) {
    case FILTER_RESOURCES.Strain:
      return { activeFilterResources: strainsAsFilterResources, loading: isStrainsLoading };
    case FILTER_RESOURCES.Category:
      return { activeFilterResources: categoriesAsFilterResources, loading: isCategoriesLoading };
    case FILTER_RESOURCES.Weight:
      return { activeFilterResources: weightsAsFilterResources, loading: isProductsLoading };
    case FILTER_RESOURCES['Product tag']:
      return { activeFilterResources: tagsAsFilterResources, loading: isTagsLoading };
    case FILTER_RESOURCES['Inventory tag']:
      return { activeFilterResources: inventoryTagsAsFilterResources, loading: isTagsLoading };
    case FILTER_RESOURCES['Pricing tier']:
      return { activeFilterResources: chargeCodesAsFilterResources, loading: isChargeCodesLoading };
    case FILTER_RESOURCES.Brand:
      return { activeFilterResources: brandsAsFilterResources, loading: isBrandsLoading };
    case FILTER_RESOURCES.Vendor:
      return { activeFilterResources: vendorsAsFilterResources, loading: isVendorsLoading };
    case FILTER_RESOURCES.Product:
      return { activeFilterResources: productsAsFilterResources, loading: isProductsLoading };
    default:
      return { activeFilterResources: [], loading: false };
  }
}
